import { useMemo, useRef, useState } from 'react';

import { PageMeta } from '@/components/PageMeta';
import * as htmlToImage from 'html-to-image';
import { BadgeCheck, Copy, Download, Image as ImageIcon, Type } from 'lucide-react';

type LogoSize = {
  label: string;
  boxPx: number; // taille du carré bleu
  radiusClass: string;
  iconClass: string; // taille du svg (Tailwind)
  containerClass?: string; // +++
};

const sizes: LogoSize[] = [
  {
    label: '16',
    boxPx: 16,
    radiusClass: 'rounded-md',
    iconClass: 'h-3.5 w-3.5',
    // pas d’ombre en 16px (favicon)
    containerClass: 'bg-blue-600 shadow-none dark:shadow-none',
  },
  { label: '32', boxPx: 32, radiusClass: 'rounded-lg', iconClass: 'h-5 w-5' },
  { label: '40', boxPx: 40, radiusClass: 'rounded-xl', iconClass: 'h-6 w-6' },
  { label: '48', boxPx: 48, radiusClass: 'rounded-xl', iconClass: 'h-7 w-7' },
  { label: '64', boxPx: 64, radiusClass: 'rounded-2xl', iconClass: 'h-9 w-9' },
  { label: '128', boxPx: 128, radiusClass: 'rounded-3xl', iconClass: 'h-16 w-16' },
];

function classNames(...v: Array<string | undefined | false>) {
  return v.filter(Boolean).join(' ');
}

async function copyToClipboard(text: string) {
  await navigator.clipboard.writeText(text);
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function BoulesConnectMark({ className }: { className?: string }) {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      preserveAspectRatio='xMidYMid meet'
      className={classNames('block shrink-0', className)}
      aria-hidden='true'
    >
      <circle cx='12' cy='12' r='10' />
      <circle cx='12' cy='12' r='1' />
    </svg>
  );
}

function LogoIcon({
  boxPx,
  radiusClass,
  iconClass,
  containerClass,
}: {
  boxPx: number;
  radiusClass: string;
  iconClass: string;
  containerClass?: string;
}) {
  return (
    <div
      className={classNames(
        // base (avec ombre)
        'flex items-center justify-center bg-blue-600 shadow-lg shadow-blue-200 dark:shadow-blue-900/50',
        // override optionnel (ex: enlever l’ombre pour 16px)
        containerClass,
        radiusClass,
      )}
      style={{ width: `${boxPx}px`, height: `${boxPx}px` }}
    >
      <BoulesConnectMark className={classNames(iconClass, 'text-white')} />
    </div>
  );
}

function buildTailwindHtml({ showTitle, size }: { showTitle: boolean; size: LogoSize }) {
  // On reste volontairement "copier/coller" Tailwind comme ton snippet.
  const divRadius =
    size.boxPx >= 128
      ? 'rounded-3xl'
      : size.boxPx >= 64
        ? 'rounded-2xl'
        : size.boxPx >= 40
          ? 'rounded-xl'
          : 'rounded-lg';

  const svgSizeClass = size.iconClass;

  const iconHtml = `<div class="flex h-[${size.boxPx}px] w-[${size.boxPx}px] items-center justify-center ${divRadius} bg-blue-600 shadow-lg shadow-blue-200 dark:shadow-blue-900/50">
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-dot ${svgSizeClass} text-white" aria-hidden="true">
    <circle cx="12" cy="12" r="10"></circle>
    <circle cx="12" cy="12" r="1"></circle>
  </svg>
</div>`;

  if (!showTitle) return iconHtml;

  return `<div class="inline-flex items-center gap-3">
  ${iconHtml}
  <div class="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">BoulesConnect</div>
</div>`;
}

function buildInlineSvgString({ fill = 'none', stroke = 'currentColor' }: { fill?: string; stroke?: string }) {
  // SVG “pur” réutilisable partout
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${fill}" stroke="${stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <circle cx="12" cy="12" r="10" />
  <circle cx="12" cy="12" r="1" />
</svg>`;
}

export default function Plv() {
  const [showTitle, setShowTitle] = useState(true);
  const [selectedSize, setSelectedSize] = useState<LogoSize>(sizes[1]); // 32
  const [toast, setToast] = useState<string | null>(null);

  // On garde un ref carré UNIQUEMENT sur l'icône, sinon à 16px ça “tord” (baseline/line-height)
  const iconRef = useRef<HTMLDivElement | null>(null);

  const htmlSnippet = useMemo(() => buildTailwindHtml({ showTitle, size: selectedSize }), [showTitle, selectedSize]);

  const svgSnippet = useMemo(() => buildInlineSvgString({}), []);

  async function onCopyHtml() {
    await copyToClipboard(htmlSnippet);
    setToast('HTML copié');
    setTimeout(() => setToast(null), 1500);
  }

  async function onCopySvg() {
    await copyToClipboard(svgSnippet);
    setToast('SVG copié');
    setTimeout(() => setToast(null), 1500);
  }

  async function onDownloadPng() {
    if (!iconRef.current) return;

    const size = selectedSize.boxPx;

    // PNG transparent + taille forcée (évite espaces “haut/bas” en 16px)
    const dataUrl = await htmlToImage.toPng(iconRef.current, {
      backgroundColor: 'transparent',
      pixelRatio: 2,
      width: size,
      height: size,
      style: {
        width: `${size}px`,
        height: `${size}px`,
        margin: '0',
        padding: '0',
      },
    });

    const res = await fetch(dataUrl);
    const blob = await res.blob();

    downloadBlob(blob, `boulesconnect-logo-${selectedSize.label}${showTitle ? '-lockup' : '-icon'}.png`);
  }

  return (
    <>
      <PageMeta
        title='Logo BoulesConnect'
        description='Récupérez le logo BoulesConnect (icône + variantes) en plusieurs tailles : copier/coller HTML, SVG et export PNG.'
        robots='noindex, follow'
      />

      {/* Hero */}
      <section className='pt-40 pb-16 px-6'>
        <div className='max-w-4xl mx-auto text-center'>
          <div className='inline-flex items-center gap-2 bg-blue-50 border border-blue-100 px-4 py-2 rounded-full mb-6'>
            <ImageIcon className='w-4 h-4 text-blue-600' />
            <span className='text-xs font-bold text-blue-700 uppercase tracking-wider'>Assets</span>
          </div>

          <h1 className='text-4xl md:text-5xl font-extrabold text-slate-900 mb-4'>Logo BoulesConnect</h1>

          <p className='text-lg text-slate-500'>Aperçu, copier/coller (HTML/SVG) et export PNG</p>
        </div>
      </section>

      {/* Content */}
      <section className='px-6 pb-20'>
        <div className='max-w-4xl mx-auto space-y-8'>
          {/* Controls */}
          <div className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
            <div className='flex items-center justify-between gap-4 flex-wrap'>
              <div>
                <h2 className='text-xl font-extrabold text-slate-900'>Générateur</h2>
                <p className='text-sm text-slate-600 mt-1'>Choisis une taille et copie le snippet.</p>
              </div>

              <div className='flex items-center gap-3'>
                <button
                  type='button'
                  onClick={() => setShowTitle(v => !v)}
                  className={classNames(
                    'inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition border',
                    showTitle
                      ? 'bg-slate-900 text-white border-slate-900 hover:bg-slate-800'
                      : 'bg-white text-slate-900 border-slate-200 hover:bg-slate-50',
                  )}
                >
                  <Type className='h-4 w-4' />
                  {showTitle ? 'Avec titre' : 'Sans titre'}
                </button>

                <button
                  type='button'
                  onClick={onDownloadPng}
                  className='inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700'
                >
                  <Download className='h-4 w-4' />
                  PNG
                </button>
              </div>
            </div>

            <div className='mt-6 flex flex-wrap gap-2'>
              {sizes.map(s => (
                <button
                  key={s.label}
                  type='button'
                  onClick={() => setSelectedSize(s)}
                  className={classNames(
                    'px-3 py-1.5 rounded-full text-xs font-bold border transition',
                    selectedSize.label === s.label
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50',
                  )}
                >
                  {s.label}px
                </button>
              ))}
            </div>

            {/* Preview */}
            <div className='mt-8'>
              <div className='text-xs font-bold text-slate-500 uppercase tracking-wider mb-3'>Aperçu</div>

              <div className='p-6 bg-slate-50 rounded-2xl border border-slate-100 overflow-auto'>
                <div className='inline-block'>
                  <div className='inline-flex items-center gap-3'>
                    {/* ref carré sur l’icône -> export PNG propre en 16px */}
                    <div ref={iconRef} className='inline-block'>
                      <LogoIcon
                        boxPx={selectedSize.boxPx}
                        radiusClass={selectedSize.radiusClass}
                        iconClass={selectedSize.iconClass}
                        containerClass={selectedSize.containerClass}
                      />
                    </div>

                    {showTitle ? (
                      <div className='text-xl font-semibold tracking-tight text-slate-900 dark:text-white'>
                        BoulesConnect
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              {toast ? (
                <div className='mt-3 inline-flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-xl'>
                  <BadgeCheck className='h-4 w-4' />
                  {toast}
                </div>
              ) : null}
            </div>
          </div>

          {/* Snippets */}
          <div className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
            <h2 className='text-xl font-extrabold text-slate-900 mb-6'>Copier / coller</h2>

            <div className='grid md:grid-cols-2 gap-4'>
              <div className='p-4 bg-slate-50 rounded-2xl border border-slate-100'>
                <div className='flex items-center justify-between gap-3 mb-3'>
                  <div className='text-sm font-bold text-slate-900'>HTML (Tailwind)</div>

                  <button
                    type='button'
                    onClick={onCopyHtml}
                    className='inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 transition'
                  >
                    <Copy className='h-4 w-4' />
                    Copier
                  </button>
                </div>

                <pre className='text-xs text-slate-700 overflow-auto'>
                  <code>{htmlSnippet}</code>
                </pre>
              </div>

              <div className='p-4 bg-slate-50 rounded-2xl border border-slate-100'>
                <div className='flex items-center justify-between gap-3 mb-3'>
                  <div className='text-sm font-bold text-slate-900'>SVG inline (pur)</div>

                  <button
                    type='button'
                    onClick={onCopySvg}
                    className='inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 transition'
                  >
                    <Copy className='h-4 w-4' />
                    Copier
                  </button>
                </div>

                <pre className='text-xs text-slate-700 overflow-auto'>
                  <code>{svgSnippet}</code>
                </pre>

                <p className='text-xs text-slate-500 mt-3'>
                  Astuce : pour une pub/print, utilise plutôt le SVG “pur” que du HTML.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
